/**
 * ConsultantDetailPage — iter638
 *
 * Дедикирана страница за един консултант. Маршрут: /consultants/:slug
 * Например /consultants/inkov или /consultants/ivanova.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import ConsultantProfileSection from "@/components/ConsultantProfileSection";
import { getConsultant } from "@/data/consultants";
import { ArrowRight, Sparkles, ArrowLeft } from "lucide-react";

const TX = {
  bg: {
    back: "Всички консултанти",
    cta_title: "Готов да изградиш своя дигитален здравен близнак?",
    cta_sub: "60 секунди безплатна оценка. Без credit card.",
    cta_btn: "Започни сега",
    not_found_title: "Консултантът не е намерен",
    not_found_sub: "Виж пълния списък с медицинските ни консултанти.",
  },
  en: {
    back: "All consultants",
    cta_title: "Ready to build your digital health twin?",
    cta_sub: "60-second free assessment. No credit card.",
    cta_btn: "Get started",
    not_found_title: "Consultant not found",
    not_found_sub: "See the full list of our medical consultants.",
  },
  es: {
    back: "Todos los consultores",
    cta_title: "¿Listo para construir tu gemelo digital de salud?",
    cta_sub: "Evaluación gratuita de 60 segundos. Sin tarjeta de crédito.",
    cta_btn: "Comenzar",
    not_found_title: "Consultor no encontrado",
    not_found_sub: "Consulta la lista completa de nuestros consultores médicos.",
  },
};

export default function ConsultantDetailPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const t = TX[language === "en" ? "en" : "bg"];
  const consultant = getConsultant(language, slug);

  if (!consultant) {
    return <Navigate to="/consultants" replace />;
  }

  // iter645 — Person + Physician schema for rich snippets in Google.
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: consultant.name,
    description: consultant.summary,
    image: consultant.photo,
    url: `https://nutramilo.com/consultants/${consultant.slug}`,
    jobTitle: consultant.role,
    worksFor: { "@type": "Organization", name: "Nutramilo", url: "https://nutramilo.com" },
    address: { "@type": "PostalAddress", addressLocality: "Sofia", addressCountry: "BG" },
    medicalSpecialty: consultant.slug === "inkov"
      ? ["Surgery", "OncologicSurgery", "Mastology", "ThoracicSurgery"]
      : ["Dentistry", "Orthodontics", "PediatricDentistry"],
    alumniOf: (consultant.edu || []).map((e) => ({ "@type": "EducationalOrganization", name: e })),
    hasCredential: (consultant.quals || []).map((q) => ({ "@type": "EducationalOccupationalCredential", name: q })),
    ...(consultant.orcid_url ? { sameAs: [consultant.orcid_url] } : {}),
    ...(consultant.booking_url
      ? {
          potentialAction: {
            "@type": "ReserveAction",
            name: "Запиши час",
            target: { "@type": "EntryPoint", urlTemplate: consultant.booking_url },
          },
        }
      : {}),
  };

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,5%)] text-slate-100" : "bg-gradient-to-b from-white via-fuchsia-50/30 to-white text-slate-900"}`}>
      <SEO
        title={`${consultant.name} | Nutramilo`}
        description={consultant.summary}
        path={`/consultants/${consultant.slug}`}
      />
      {/* iter645 — Person/Physician structured data */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      </Helmet>
      <SubpageNav />

      <header className={`border-b ${isDark ? "border-[hsl(223,47%,12%)]" : "border-slate-100"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-4">
          <Link
            to="/consultants"
            data-testid="consultant-back-link"
            className={`inline-flex items-center gap-2 text-xs font-semibold ${isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-[#287a2e]"}`}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t.back}
          </Link>
        </div>
      </header>

      <ConsultantProfileSection consultant={consultant} testIdPrefix={`consultant-${consultant.slug}`} />

      {/* CTA */}
      <section className={`py-16 sm:py-20 ${isDark ? "bg-[hsl(223,47%,5%)]" : "bg-white"}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-4" style={{ color: "var(--brand-pink)" }} />
          <h2 className={`text-3xl sm:text-4xl font-semibold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {t.cta_title}
          </h2>
          <p className={`mb-7 text-base ${isDark ? "text-slate-400" : "text-slate-600"}`}>{t.cta_sub}</p>
          <Link
            to="/register"
            data-testid="consultant-cta-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white shadow-md hover:shadow-lg transition-shadow"
            style={{ background: "var(--brand-gradient-primary)" }}
          >
            {t.cta_btn}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <SubpageFooter />
    </div>
  );
}

export function ConsultantNotFoundFallback() {
  // Optional fallback view if we ever want a non-redirect 404 page.
  const { language } = useLanguage();
  const t = TX[language === "en" ? "en" : "bg"];
  return (
    <div className="min-h-screen flex flex-col">
      <SubpageNav />
      <main className="flex-1 flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-3xl font-bold mb-2">{t.not_found_title}</h1>
        <p className="opacity-70 mb-6">{t.not_found_sub}</p>
        <Link to="/consultants" className="px-6 py-3 rounded-full text-sm font-bold text-white" style={{ background: "var(--brand-gradient-primary)" }}>
          {t.back}
        </Link>
      </main>
      <SubpageFooter />
    </div>
  );
}
